import { Head } from "@inertiajs/react"
import { useState } from "react"

import AddAgentEntry from "@/components/agents/add-agent-entry"
import AdminAgentChangelog from "@/components/agents/admin-agent-changelog"
import TextLink from "@/components/text-link"
import AttributeDisplay from "@/components/ui/attribute-display"
import AttributesContainer from "@/components/ui/attributes-container"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import RecordPageHeader, {
  type RecordPageHeaderAction,
} from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import { INTEGRATION_REGISTRY } from "@/lib/integration-registry"
import { cn, formatCurrency } from "@/lib/utils"
import {
  adminAccountPath,
  adminAgentPath,
  adminAgentsPath,
  editAdminAgentPath,
} from "@/routes"
import type {
  Agent,
  AvailableModel,
  AvailableSkill,
  BreadcrumbItem,
} from "@/types"

export default function ShowAgent({
  agent,
  available_skills = [],
  available_models = [],
}: {
  agent: Agent
  available_skills?: AvailableSkill[]
  available_models?: AvailableModel[]
}) {
  const [isAddEntryOpen, setIsAddEntryOpen] = useState(false)

  // Standard agent instances derive most config from their archetype, so the
  // admin-side surface area is intentionally narrower than custom agents.
  const isStandard = agent.agent_archetype != null

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Agents",
      href: adminAgentsPath(),
    },
    {
      title: agent.name,
      href: adminAgentPath({ id: agent.uid }),
    },
  ]

  const actions: RecordPageHeaderAction[] = isStandard
    ? []
    : [
        {
          label: "Add update",
          onClick: () => {
            setIsAddEntryOpen(true)
          },
        },
      ]

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />
      <div
        className={cn(
          "mx-auto flex h-full w-full flex-col gap-6 p-4",
          isStandard && "max-w-3xl",
        )}
      >
        <RecordPageHeader
          title={agent.name}
          state={agent.state}
          editPath={editAdminAgentPath({ id: agent.uid })}
          deletePath={adminAgentPath({ id: agent.uid })}
          actions={actions}
        />

        <div
          className={cn(
            "grid grid-cols-1 gap-6",
            !isStandard && "lg:grid-cols-4",
          )}
        >
          {/* Details Section - 3/5 width on desktop */}
          <div className={cn(!isStandard && "lg:col-span-2")}>
            <div className="space-y-3">
              <h2 className="text-base font-semibold">Details</h2>
              <AttributesContainer className="gap-6">
                <AttributeDisplay
                  label="Account"
                  value={
                    agent.account_uid ? (
                      <TextLink
                        href={adminAccountPath({ id: agent.account_uid })}
                        as="div"
                        prefetch
                      >
                        {agent.account?.name}
                      </TextLink>
                    ) : null
                  }
                />
                {isStandard && agent.agent_archetype && (
                  <AttributeDisplay
                    label="Archetype"
                    value={agent.agent_archetype.name}
                  />
                )}
                {!isStandard && (
                  <>
                    <AttributeDisplay label="Summary" value={agent.summary} />
                    <AttributeDisplay
                      label="Problem solved"
                      value={agent.problem_solved}
                    />
                  </>
                )}
                <AttributeDisplay
                  label="Agent description"
                  value={agent.description}
                />
                {agent.slug && (
                  <AttributeDisplay label="Slug" value={agent.slug} />
                )}
                {agent.model_id && (
                  <AttributeDisplay
                    label="Model"
                    value={
                      available_models.find((m) => m.id === agent.model_id)
                        ?.name ?? agent.model_id
                    }
                  />
                )}
                {agent.launch_title && (
                  <AttributeDisplay
                    label="Launch title"
                    value={agent.launch_title}
                  />
                )}
                {agent.launch_prompt && (
                  <AttributeDisplay
                    label="Launch prompt"
                    value={agent.launch_prompt}
                  />
                )}
                {agent.launch_icon && (
                  <AttributeDisplay
                    label="Launch icon"
                    value={agent.launch_icon}
                  />
                )}
                {!isStandard && (
                  <>
                    <AttributeDisplay
                      label="Manual time per task"
                      value={
                        agent.manual_task_time_in_minutes
                          ? `${agent.manual_task_time_in_minutes} minutes`
                          : ""
                      }
                    />
                    <AttributeDisplay
                      label="Hourly rate"
                      value={formatCurrency(agent.hourly_rate ?? 0)}
                    />
                    <AttributeDisplay
                      label="Unique metrics"
                      value={
                        <div className="divide-border -mt-2 divide-y">
                          {agent.agent_metrics?.map((metric) => (
                            <div key={metric.uid} className="py-2">
                              <div className="">
                                <div className="text-sm font-medium">
                                  {metric.label}
                                </div>
                                <div className="text-muted-foreground text-sm">
                                  {metric.description}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                    />
                  </>
                )}
                {agent.agent_integrations &&
                  agent.agent_integrations.length > 0 && (
                    <AttributeDisplay
                      label="Integrations"
                      value={
                        <div className="-mt-1 space-y-2">
                          {agent.agent_integrations.map((kind) => {
                            const registry = INTEGRATION_REGISTRY[kind]
                            return (
                              <div
                                key={kind}
                                className="flex items-center gap-3 rounded-lg border px-4 py-3"
                              >
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                                  {registry?.icon ?? (
                                    <span className="text-muted-foreground text-xs">
                                      {kind}
                                    </span>
                                  )}
                                </div>
                                <span className="text-foreground text-sm font-medium">
                                  {registry?.label ?? kind}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      }
                    />
                  )}
                {agent.skills && agent.skills.length > 0 && (
                  <AttributeDisplay
                    label="Skills"
                    value={
                      <div className="-mt-1 space-y-1">
                        {agent.skills.map((skillType) => {
                          const skill = available_skills.find(
                            (s) => s.type === skillType,
                          )
                          return (
                            <div key={skillType} className="py-1">
                              <div className="text-sm font-medium">
                                {skill?.label ?? skillType}
                              </div>
                              {skill?.description && (
                                <div className="text-muted-foreground text-sm">
                                  {skill.description}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    }
                  />
                )}
                {agent.agent_resources &&
                  Object.keys(agent.agent_resources).length > 0 && (
                    <AttributeDisplay
                      label="Resources"
                      value={
                        <div className="divide-border -mt-2 divide-y">
                          {Object.entries(agent.agent_resources).flatMap(
                            ([kind, resources]) => {
                              const registry = INTEGRATION_REGISTRY[kind]
                              const grouped = new Map<
                                string,
                                { label: string; names: string[] }
                              >()
                              for (const resource of resources) {
                                const rt = registry?.resourceTypes.find(
                                  (t) => t.value === resource.resource_type,
                                )
                                const label =
                                  rt?.label ?? resource.resource_type
                                if (!grouped.has(label)) {
                                  grouped.set(label, { label, names: [] })
                                }
                                grouped.get(label)!.names.push(resource.name)
                              }
                              return Array.from(grouped.values()).map(
                                (group) => (
                                  <div
                                    key={`${kind}-${group.label}`}
                                    className="py-2"
                                  >
                                    <div className="text-foreground text-sm font-medium">
                                      {group.label}s
                                    </div>
                                    {group.names.map((name, i) => (
                                      <div
                                        key={i}
                                        className="text-muted-foreground text-sm"
                                      >
                                        {name}
                                      </div>
                                    ))}
                                  </div>
                                ),
                              )
                            },
                          )}
                        </div>
                      }
                    />
                  )}
              </AttributesContainer>
            </div>
          </div>

          {/* Updates Section - 2/5 width on desktop, below details on mobile */}
          {!isStandard && (
            <div className="lg:col-span-2">
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle>Imperative updates</CardTitle>
                </CardHeader>
                <CardContent>
                  <AdminAgentChangelog
                    entries={agent.agent_changelog_entries ?? []}
                    agent={agent}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      {!isStandard && (
        <AddAgentEntry
          agent={agent}
          open={isAddEntryOpen}
          onOpenChange={setIsAddEntryOpen}
        />
      )}
    </AdminLayout>
  )
}
